'use client';

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect, useState } from 'react';
import { VIDEO_COVER_FALLBACK_SECONDS } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/constants';
interface VideoBufferingOptions {
  videoRef: RefObject<HTMLVideoElement | null>;
  // Only while the clip is meant to be playing. A paused or finished clip
  // fires `waiting` too, and that is nothing the viewer is waiting on.
  enabled: boolean;
}

// A clip that started and then ran dry mid-shot -- `waiting`, the playhead
// actually stopped. Not `stalled`: that only says the download paused, and
// fires happily while the clip plays on from its buffer. Raised only once the
// stall has outlasted VIDEO_COVER_FALLBACK_SECONDS, so a hiccup the
// viewer barely sees never brings the loading veil up over it.
export const useVideoBuffering = t0 => {
  const $ = _c(4);
  const {
    videoRef,
    enabled
  } = t0;
  const [isBuffering, setIsBuffering] = useState(false);
  let t1;
  let t2;
  if ($[0] !== enabled || $[1] !== videoRef) {
    t1 = () => {
      const element = videoRef.current;
      if (!element || !enabled) {
        setIsBuffering(false);
        return;
      }
      let timer = 0;
      const clearTimer = () => {
        if (timer) {
          window.clearTimeout(timer);
          timer = 0;
        }
      };
      const handleStall = () => {
        if (timer) {
          return;
        }
        timer = window.setTimeout(() => {
          timer = 0;
          setIsBuffering(true);
        }, VIDEO_COVER_FALLBACK_SECONDS * 1000);
      };
      const handleResume = () => {
        clearTimer();
        setIsBuffering(false);
      };
      element.addEventListener("waiting", handleStall);
      element.addEventListener("playing", handleResume);
      element.addEventListener("canplaythrough", handleResume);
      return () => {
        clearTimer();
        element.removeEventListener("waiting", handleStall);
        element.removeEventListener("playing", handleResume);
        element.removeEventListener("canplaythrough", handleResume);
      };
    };
    t2 = [videoRef, enabled];
    $[0] = enabled;
    $[1] = videoRef;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return isBuffering;
};